import React, { useMemo, useState } from "react";
import { motion } from "framer-motion";
import { Draggable } from "@hello-pangea/dnd";
import { BarChart2, User } from "lucide-react";
import DealCard from "./dealcard";
import { CircularProgressbar, buildStyles } from "react-circular-progressbar";
import "react-circular-progressbar/dist/styles.css";
import InnerLoader from "../ui/loader/inner-loader";

// Skeleton
const Skeleton = ({ className = "" }) => (
  <div className={`animate-pulse bg-slate-200 rounded-md ${className}`} />
);

const getItemStyle = (style, snapshot) => ({
  ...style,
  transform:
    snapshot.isDragging && style?.transform
      ? `${style.transform} rotate(2deg)`
      : style?.transform,
});

function StageColumn({
  stage,
  deals,
  provided,
  onDealClick,
  isLoading,
  totalPipelineDeals = 0,
  dragEnabled = true,
  isLast = false,
  cardLoad,
  colors,
   PackageList = [],
  DealStages=[],
  ProductList = [],
  ClientList = [],
  UserList = [],
  contactId = {},
  onDealUpdated = () => {}, // refresh after update
  onSubmit = () => {},
  onError = () => {},
   onLoadMoreClients={},
                hasMoreClients={},
                page={},
                isLoadingClients={}
}) {
  const [showCircle, setShowCircle] = useState(false);

  const totalValue = useMemo(
    () => deals.reduce((sum, deal) => sum + (deal.deal_value || 0), 0),
    [deals]
  );

  const sortedDeals = useMemo(
    () => [...deals].sort((a, b) => (a.order ?? 0) - (b.order ?? 0)),
    [deals]
  );

  // correct percent calculation (guard divide-by-zero)
  const percent =
    totalPipelineDeals > 0
      ? Math.round((deals.length / totalPipelineDeals) * 100)
      : 0;

  // dynamic font sizing so e.g. 100% fits
  const getPercentTextClass = (p) => {
    if (p >= 100) return "text-xs";
    if (p >= 10) return "text-sm";
    return "text-base";
  };

  return (
    <div
      className={`h-full flex flex-col relative gap-y-2 ${
        !isLast ? "border-r-2 border-[#E1E4EA] pr-6 " : "mr-6"
      } `}
    >
      {/* Column Header */}
      <motion.div
        // className={`rounded-2xl p-6 mb-4 border-2 transition-all sticky z-[49] top-0 duration-200 bg-white ${stage?.borderColor}  border-dashed`}
        // whileHover={{ scale: 1.02 }}
        className={`rounded-[10px] p-4 border border-[#E1E4E9] bg-[#fff] sticky z-[49] top-0 `}
      >
        <div className="flex flex-col items-center gap-5 self-stretch w-full">
          <div className="flex items-center justify-between self-stretch px-[3px]">
            <div className="flex items-center gap-3">
              {isLoading ? (
                <Skeleton className="h-6 w-3/4 mb-2" />
              ) : (
                <div className="flex gap-1 items-center">
                  <span
                    className={`w-3 h-3 rounded bg-[${
                      colors?.color ? colors.color : "#9D5FEF"
                    }] `}
                    style={
                      colors?.color
                        ? { backgroundColor: colors?.color }
                        : { backgroundColor: "#9D5FEF" }
                    }
                  ></span>
                  <h3 className={`font-bold text-sm uppercase tracking-wider`}>
                    {stage.name}
                  </h3>
                </div>
              )}
            </div>

            <div className="text-[#0E121B] text-[18px] leading-5 font-semibold">
              {stage?.total_count}
            </div>
          </div>

          <div className="w-full h-[1px] bg-[#E1E4EA] shadow-lg "></div>

          {/* <div className={`text-xs font-semibold ${stage?.textColor} opacity-75`}>
                    ${stage.total_amount} total
                </div> */}

          <div className="flex h-9 px-3 py-4 justify-between items-center self-stretch rounded-md border border-[#e1e4ea] bg-[#f7f7f7] ">
            <div className="flex gap-1 items-center">
              <User size={16} color="#a09c9c" strokeWidth={2.5} />
              <span className="text-[#0E121B] text-[14px] font-semibold leading-9 flex gap-1">
                Total
              </span>
            </div>
            <div>${stage.total_amount}</div>
          </div>
        </div>
      </motion.div>

      {/* Cards Area */}
      <div
        ref={provided.innerRef}
        {...provided.droppableProps}
        // className="flex-1 p-2 rounded-xl bg-slate-50 border border-slate-200 shadow-inner"
        // style={{ height: "calc(100vh - 250px)", overflow: "auto" }}
        className="flex p-[10px]   flex-col items-center gap-5 bg-[#E1E4EA] rounded-xl   h-full overflow-auto"
      >
        <div className="space-y-2 min-h-[200px] overflow-hidden scrollbar-thin scrollbar-thumb-slate-300 scrollbar-track-transparent h-full w-full">
          {isLoading
            ? Array(3)
                .fill(0)
                .map((_, i) => (
                  <div
                    key={i}
                    className="bg-white rounded-lg p-4 border border-slate-200 shadow-sm"
                  >
                    <Skeleton className="h-6 w-3/4 mb-2" />
                    <Skeleton className="h-4 w-1/2 mb-3" />
                    <Skeleton className="h-4 w-full" />
                  </div>
                ))
            : sortedDeals.map((deal, index) =>
                dragEnabled ? (
                  <Draggable
                    key={deal.id}
                    draggableId={deal.id}
                    index={index}
                    isDragDisabled={["won", "lost", "stalled"].includes(
                      String(stage.name || "").toLowerCase()
                    )}
                  >
                    {(provided, snapshot) => (
                      <div
                        ref={provided.innerRef}
                        {...provided.draggableProps}
                        {...provided.dragHandleProps}
                        className={`cursor-pointer transition-transform duration-150 ${
                          snapshot.isDragging
                            ? "scale-105 shadow-lg rotate-1"
                            : ""
                        }`}
                        style={getItemStyle(
                          provided.draggableProps.style,
                          snapshot
                        )}
                      >
                        <DealCard
                          deal={deal}
                          onClick={onDealClick}
                          isDragging={snapshot.isDragging}
                          accentHexColor={stage?.hexColor}
                          DealStages={DealStages}
                          PackageList={PackageList}
                          ProductList={ProductList}
                          ClientList={ClientList}
                          UserList={UserList}
                          contactId={contactId}
                          onDealUpdated={onDealUpdated} // refresh after update
                          onSubmit={onSubmit}
                          onError={onError}
                           onLoadMoreClients={onLoadMoreClients}
                                        hasMoreClients={hasMoreClients}
                                        page={page}
                                        isLoadingClients={isLoadingClients}
                        />
                      </div>
                    )}
                  </Draggable>
                ) : (
                  <div key={deal.id}>
                    <DealCard
                      deal={deal}
                      onClick={onDealClick}
                      accentHexColor={stage?.hexColor}
                    />
                  </div>
                )
              )}
          {stage?.total_count > deals?.length && cardLoad ? (
            <div className="">
              <InnerLoader
                textStatus={false}
                className={"!w-5 !h-5 !border-[3px]"}
              />
            </div>
          ) : null}

          {/* {provided.placeholder} */}

          {!isLoading && deals.length === 0 && (
            <div className="text-center py-6 text-slate-400 font-medium">
              No deals in this stage
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default React.memo(StageColumn);
